/**
 * @fileoverview Modelo de datos para gestión integral de clientes en Mercately CRM
 *
 * Este modelo encapsula todas las operaciones de API relacionadas con clientes,
 * incluyendo CRUD básico, búsqueda avanzada, gestión de campos personalizados
 * y operaciones específicas del retailer. Construido sobre abstractModel para
 * consistencia de interfaz y manejo de errores estandarizado.
 *
 * @module CustomersModelMc
 * @category API Model - Data Layer
 * @since 1.0.0
 * @version 3.1.0 - Añadida funcionalidad de tags y campos personalizados
 */
import abstractModel from '../../core/MercatelyModel';
import { MERCATELY_API } from '../../constants/AppConstants';
import { msInboxApiUrl } from '../../services/inbox/msInboxClient';
import { resolveMsApiPlatform } from '../../services/inbox/inboxMsChatPlatform';

const resource = `${MERCATELY_API}/customers`;

const model = abstractModel(resource);

model.search = async (params) => model.get(params, { path: `${resource}/search_customers` });

model.createFromRetailer = async (params) => model.save(params, { path: `${resource}/create_from_retailer` });

// Actualizar datos del cliente desde el retailer
model.updateFromRetailer = async (params) => model.save(params, { path: `${resource}/update_from_retailer` });

// Obtener un cliente por ID
model.getOne = async (params, queryParams = {}) => model.findById(params.id, queryParams);

// Custom Fields related to Customers
model.getCustomerFields = async (customerId) => {
  const path = `${resource}/${customerId}/custom_fields`;
  return model.get({}, { path });
}

// Tags related to Customers
model.addCustomerTags = async (customerId, body) => {
  const newResource = `${resource}/${customerId}/add_tags`;
  return model.save(body, { path: newResource, method: 'POST' });
}

// Conversational Topics related to Customers
model.fetchCustomerTopics = async ({ customerId, platform, page }) => {
  const newResource = `${resource}/${customerId}/conversation_topics?channel=${platform}&page=${page}`;
  return model.get({}, { path: newResource });
};

// Delete a Customer Conversational Topic
model.deleteCustomerTopic = async ({ customerId, topicId }) => {
  const newResource = `${resource}/${customerId}/conversation_topics/${topicId}`;
  return model.remove({}, { path: newResource });
};

// Assign a Conversational Topic to a Customer
model.assignCustomerTopic = async (params) => {
  const newResource = `${resource}/${params.customerId}/conversation_topics`;
  return model.save(params, { path: newResource });
};

// Tópicos de chats gestionados por el microservicio de inbox
model.getMicroserviceCustomerTopics = async ({ chatId, platform, page }) => {
  const msPlatform = resolveMsApiPlatform(platform);
  if (!msPlatform) {
    return { topics: [], total: 0, total_pages: 1, page: 1 };
  }
  return model.get({}, { path: msInboxApiUrl(`/chats/inbox/${chatId}/${msPlatform}/topics?page=${page}`) });
};

// Eliminar un tópico en el microservicio de inbox
model.deleteMicroserviceCustomerTopic = async ({ chatId, platform, topicId }) => {
  const msPlatform = resolveMsApiPlatform(platform);
  if (!msPlatform) {
    return null;
  }
  return model.remove({}, { path: msInboxApiUrl(`/chats/inbox/${chatId}/${msPlatform}/topics/${topicId}`) }, {});
};

// Asignar un tópico en el microservicio de inbox
model.assignMicroserviceCustomerTopic = async ({ chatId, platform, topicable_type, topicable_id }) => {
  const msPlatform = resolveMsApiPlatform(platform);
  if (!msPlatform) {
    return null;
  }
  const body = { topicable_type, topicable_id };
  return model.save(body, { path: msInboxApiUrl(`/chats/inbox/${chatId}/${msPlatform}/topics`), method: 'POST' });
};

// Customer Addresses related to Customers
model.getCustomerAddresses = async (webId) => {
  const newResource = `${resource}/${webId}/customer_addresses`;
  return model.get({}, { path: newResource });
};

// Remove a Customer Address
model.removeCustomerAddresses = async (webId, params) => {
  return model.remove(params, { path: `${resource}/${webId}/customer_addresses` });
};

model.createCustomerAddress = async (webId, params) => {
  const newResource = `${resource}/${webId}/customer_addresses`;
  return model.save(params, { path: newResource });
}

model.getCustomerNotes = async ({ customerId, platform, customer_fb_post_id }) => {
  const newResource = `${resource}/${customerId}/notes?platform=${platform}&customer_fb_post_id=${customer_fb_post_id}`;
  return model.get({}, { path: newResource });
};

// Notas de chats gestionados por el microservicio de inbox (Messenger multipágina, TikTok, etc.)
model.getMicroserviceCustomerNotes = async ({ chatId, platform }) => {
  const msPlatform = resolveMsApiPlatform(platform);
  if (!msPlatform) {
    return null;
  }
  return model.get({}, { path: msInboxApiUrl(`/chats/inbox/${chatId}/${msPlatform}/notes`) });
};

// Save a Customer Note
model.saveCustomerEvent = async (webId, params) => {
  const newResource = `${resource}/${webId}/customer_events`;
  return model.save(params, { path: newResource });
};

export default model;
